import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Order } from "../../domain/order";
import { useUser } from "@/backend/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsAdmin } from "@/context/admin/use-is-admin";
import { HttpOrdersRepository } from "../../application/Orders";
import { client } from "@/client";

interface OrderContextType {
  orders: Order[];
  isLoading: boolean;
  error: any;
  createOrder: any;
  updateOrder: any;
  deleteOrder: any;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

interface OrderProviderProps {
  children: ReactNode;
}
const repository = new HttpOrdersRepository();
export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const { shopper } = useUser();
  const shopperId = shopper?.shopperId as string;
  const queryClient = useQueryClient();
  const id = shopper?.store.id as string;

  const isAdmin = useIsAdmin();
  // Estado para las órdenes en tiempo real
  const [liveOrders, setLiveOrders] = useState<Order[]>([]);

  // Carga inicial con react-query
  const {
    data: baseOrders = [],
    isLoading,
    error,
  } = useQuery<Order[]>({
    queryKey: ["orders", shopperId, isAdmin],
    queryFn: () => repository.getAll(isAdmin ? id : shopperId, isAdmin),
  });
  
  // Referencia para controlar la conexión WebSocket
  const wsRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef(false);

  // WebSocket: Actualizaciones en tiempo real
  useEffect(() => {
    if (!shopperId) return;

    const connect = () => {
      // Evitar múltiples conexiones
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        isConnectingRef.current
      ) {
        return;
      }

      isConnectingRef.current = true;

      const param = isAdmin ? `storeId=${id}` : `shopperId=${shopperId}`;
      wsRef.current = new WebSocket(
        `${client.OPENBASS}/v1/orders/sync?${param}`
      );

      wsRef.current.onopen = () => {
        console.log("🟢 WebSocket conectado");
        isConnectingRef.current = false;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const order = JSON.parse(event.data) as { fullDocument: Order };
          //@ts-ignore
          order.fullDocument.id = order?.fullDocument?._id;
          //@ts-ignore
          if (order?.fullDocument?.id) {
            setLiveOrders((prev) => {
              const updated = [...prev];
              const index = updated.findIndex(
                //@ts-ignore
                (o) => o?.id == order.fullDocument.id
              );
              if (index != -1) {
                updated[index] = order.fullDocument;
              } else {
                updated.push(order.fullDocument);
              }
              return updated;
            });
          }
        } catch (err) {
          console.error("❌ Error parseando mensaje WebSocket:", err);
        }
      };

      wsRef.current.onerror = (err) => {
        console.error("⚠️ Error WebSocket:", err);
        isConnectingRef.current = false;
      };

      wsRef.current.onclose = (e) => {
        console.warn("🔴 WebSocket cerrado, reconectando...");
        isConnectingRef.current = false;
        // Reconecta después de 2 segundos si no se cerró manualmente
        if (e.code !== 1000) {
          setTimeout(connect, 2000);
        }
      };
    };

    if (baseOrders.length != 0) {
      if (baseOrders.filter((x) => x.status != "COMPLETED").length != 0) {
        connect();
      }
    }

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, "Cierre voluntario del cliente");
      }
      isConnectingRef.current = false;
    };
  }, [baseOrders.length, shopperId]);

  // Combina órdenes iniciales + en vivo
  const orders = useMemo(() => {
    const map = new Map<string, Order>();

    for (const order of baseOrders) {
      if (order && order.id) {
        map.set(order.id, order);
      }
    }
    for (const order of liveOrders) {
      if (order && order.id) {
        map.set(order.id, order);
      }
    }

    return Array.from(map.values()).filter(Boolean);
  }, [baseOrders, liveOrders]);

  // Mutaciones (CRUD)
  const createOrder = useMutation({
    mutationFn: (order: Order) => repository.create(order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", shopperId] });
    },
  });

  const updateOrder = useMutation({
    mutationFn: ({ id, order }: { id: string; order: Partial<Order> }) =>
      repository.update(id, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", shopperId] });
    },
  });

  const deleteOrder = useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", shopperId] });
    },
  });

  const value = {
    orders,
    isLoading,
    error,
    createOrder,
    updateOrder: updateOrder.mutateAsync,
    deleteOrder,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
